import type Store from "@ember-data/store";
import type ModelRegistry from "@ember-data/types/registries/model";
import { assert } from "@ember/debug";
import { service } from "@ember/service";
import { isTesting } from "@embroider/macros";
import { tracked } from "@glimmer/tracking";
import Ember from "ember";
import SessionService from "ember-simple-auth/services/session";
import Medic from "parog-web/models/medic";
import Patient from "parog-web/models/patient";
import { removeItemFromLocalStorage } from "parog-web/utils/use-local-storage";
import type ProSanteConnect from "./pro-sante-connect";

/**
 * A subclass of the Ember Simple Auth session service.
 *
 * Ember Simple Auth provides a mechanism to  make sure a user is authenticated
 * before entering some routes, and the logic for authenticating a user.
 *
 * This subclass adds a step in the authentication process: the retrieval of the
 * authenticated medic or patient object, which contains useful information used
 * throughout the app.
 *
 * This subclass also overrides the default behaviour of the `authenticate` and
 * `handleAuthentication` methods. If the authentication is successful,
 * `authenticate` emits an event that is captured by the session service itself
 * to run a transition to the logged in route, though these events are considered
 * obsolete.
 * This has a drawback: we cannot make sure that the user is loaded before
 * entering authenticated routes.
 * When the event is emitted, it actually calls `handleAuthentication`. This subclass
 * overrides the method so that it does nothing if the medic or patient is not retrieved,
 * and an explicit call to this method is done in `authenticate` after the user is retrieved.
 * We thus have the expected behaviour on our side while still being able to use Ember Simple Auth
 * as expected.
 */
export default class SemeiaSessionService extends SessionService {
  @service declare store: Store;
  @service declare proSanteConnect: ProSanteConnect;

  @tracked declare patient?: Patient;
  @tracked declare medic?: Medic & { uuid?: string };
  @tracked authenticatedAtThisSession = false;

  /**
   * This is a temporary storage of an access token.
   * It is used while authenticating (not restoring a session): when authenticating,
   * the token is retrieved, then a call to fetchAuthenticatedUser is made.
   * But the whole authentication processed is not finished, thus this.session.authenticated is empty.
   * This attribute is used in the application adapter for authenticated requests.
   * `access_token` is used as a temporary storage, used instead of this.session.authenticated if present.
   */
  access_token?: string;

  handleInvalidation(routeAfterInvalidation): void {
    removeItemFromLocalStorage(this._selectedFilterKey);

    if (isTesting()) {
      // When testing, Ember Simple Auth does not reload the page. Thus in-memory variable must be reset.
      this.medic = undefined;
      this.patient = undefined;
      this.authenticatedAtThisSession = false;
    }

    super.handleInvalidation(routeAfterInvalidation);
  }

  /**
   * Utility method for fetching an authenticated user.
   */
  async fetchAuthenticatedUser(data: {
    as: string;
    id: number | string;
    access_token?: string;
  }): Promise<void> {
    assert(
      "data.as must be patient or medic",
      ["patient", "medic"].includes(data.as)
    );
    // This assert is to ensure we don't see https://semeia.sentry.io/issues/5743948091 again.
    assert("data.id is set", data.id);

    this.access_token = data.access_token;

    const authenticatedAs = data.as;

    if (authenticatedAs === "medic") {
      this.medic = await this.store.findRecord("medic", data.id, {
        include: [
          "health-centres.dmp-health-centre-config",
          "health-centres.health-centre-tags"
        ].join(",")
      });
    } else if (authenticatedAs === "patient") {
      this.patient = await this.store.findRecord("patient", data.id, {
        include: "health-centre"
      });
    }
  }

  get logoutUrl(): string {
    return this.data.authenticated?.psc_id_token
      ? this.proSanteConnect.logoutUrl
      : "/medics-web/logout";
  }

  get user(): ModelRegistry["medic"] | ModelRegistry["patient"] | undefined {
    return this.medic ?? this.patient;
  }

  get productName() {
    if (this.user instanceof Patient) {
      return this.user?.healthCentre.productNameCapitalized ?? "Wise";
    }
    if (this.user instanceof Medic) {
      return this.user?.primaryHealthCenter?.productNameCapitalized ?? "Wise";
    }
    return "Wise";
  }

  private get _selectedFilterKey(): string {
    const key = "medics-web.protected.updates.selectedFilter";
    if (Ember.testing) {
      return `test-${key}`;
    }
    return key;
  }
}

export interface SemeiaMedicSession extends SemeiaSessionService {
  medic: Medic;
}

export interface SemeiaPatientSession extends SemeiaSessionService {
  patient: Patient;
}

declare module "@ember/service" {
  interface Registry {
    session: SemeiaSessionService;
  }
}
